import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useUserPickupOrders } from '@/api/user-package/queries/useUserPickupOrders'
import { PkgWarehouse, UserPkg } from '@/api/user-package/types/UserPkg'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import ClientPkgIlustration from '@/components/icons/client-pkg-ilustration.svg?react'
import InfoIcon from '@/components/icons/info.svg?react'
import { SegmentedButton } from '@/components/SegmentedButton'
import { getPickupOrderStatus, getPkgStatus, isPkgUniteable } from '@/helpers/pkgUtils'
import { RoutePath } from '@/RoutePath'

import { useUserPkgs } from '../../../api/user-package/queries/useUserPkgs'
import { Spinner } from '../../../components/ui/spinner'
import { ClientPkgFilterState, ClientPkgsFilter, ViewEnum } from './components/ClientPkgsFilter'
import { PickupOrderPreview } from './components/PickupOrderPreview'
import { PkgPreview } from './components/PkgPreview'
import EmptyPkgsIcon from './empty-pkgs.svg?react'

export default function ClientPackagesPage() {
  const { data: userPkgs, isLoading: isLoadingUserPkgs } = useUserPkgs()
  const [filter, setFilter] = useState<ClientPkgFilterState>({ warehouses: [], status: 'all' })
  const [selectedTab, setSelectedTab] = useState<ViewEnum>(ViewEnum.All)
  const [splitPkg, setSplitPkg] = useState<UserPkg | null>(null)
  const { data: userPickupOrders } = useUserPickupOrders()
  const navigate = useNavigate()
  const segmentedOptions = [
    { value: ViewEnum.All, label: 'כל החבילות' },
    { value: ViewEnum.Shipped, label: 'חבילות שנשלחו' },
    // { value: ViewEnum.Identify, label: 'חבילות לזיהוי', badge: 3 },
  ]
  const pkgsByTab = useMemo(() => {
    return userPkgs?.filter(pkg => {
      if (selectedTab === ViewEnum.Identify) return pkg.status === 'needs_identification'
      if (selectedTab === ViewEnum.Shipped) return pkg.isShipped
      return true
    })
  }, [userPkgs, selectedTab])

  const isUniteShown = useMemo(() => {
    return userPkgs?.some(isPkgUniteable)
  }, [userPkgs])

  const filteredPackages = useMemo(
    () =>
      pkgsByTab?.filter(pkg => {
        if (filter.warehouses.length > 0 && !filter.warehouses.includes(pkg.warehouse)) {
          return false
        }
        if (filter.status !== 'all') {
          return getPkgStatus(pkg) === filter.status
        }
        // For ViewEnum.All tab, exclude shipped packages
        if (filter.status === 'all' && selectedTab === ViewEnum.All) {
          return !pkg.isShipped
        }
        return true
      }) || [],
    [pkgsByTab, filter.warehouses, filter.status, selectedTab]
  )

  const availableWarehouses = useMemo(() => {
    const res = new Set(pkgsByTab?.map(pkg => pkg.warehouse).filter(Boolean))
    if (userPickupOrders?.length) {
      res.add(PkgWarehouse.UK)
    }
    return Array.from(res)
  }, [pkgsByTab, userPickupOrders])

  const isSplitPkg = useCallback(
    (pkg: UserPkg) => {
      // If a package shares the same tracking number with another package, it is free to unite
      return userPkgs?.some(p => p.fromTrackingNumber === pkg.fromTrackingNumber && p.id !== pkg.id)
    },
    [userPkgs]
  )
  const filteredPickupOrders = useMemo(() => {
    return userPickupOrders?.filter(order => {
      if (filter.warehouses.length > 0 && !filter.warehouses.includes(PkgWarehouse.UK)) {
        return false
      }
      if (filter.status !== 'all') {
        return getPickupOrderStatus(order) === filter.status
      }
      return true
    })
  }, [userPickupOrders, filter.warehouses, filter.status])

  const pkgStatuses = useMemo(() => {
    const statuses = selectedTab === ViewEnum.All ? pkgsByTab?.filter(pkg => !pkg.isShipped) : pkgsByTab
    const res = new Set<string>(statuses?.map(pkg => getPkgStatus(pkg)) || [])
    if (userPickupOrders?.length) {
      userPickupOrders.forEach(order => {
        const status = getPickupOrderStatus(order)
        if (status) {
          res.add(status)
        }
      })
    }
    return Array.from(res).sort()
  }, [pkgsByTab, selectedTab, userPickupOrders])

  const handlePackagePayment = (pkg: UserPkg) => {
    // Find the package and store it for the receipt upload page
    if (isSplitPkg(pkg)) {
      setSplitPkg(pkg)
      return
    }
    navigate(`${RoutePath.ReceiptUpload}/${pkg.id}`)
  }
  const onSetSelectedTab = (value: ViewEnum) => {
    setSelectedTab(value)
    setFilter({ warehouses: [], status: 'all' })
  }
  return (
    <div className='mx-auto max-w-5xl px-5 py-4 md:px-6 md:py-5' dir='rtl'>
      {/* Main Content */}
      <div className='space-y-4'>
        {/* Header Section */}
        <div className='flex items-center gap-4'>
          <ClientPkgIlustration />
          <div>
            <h2 className='text-text-primary text-lg font-medium'>כאן תמצא את כל החבילות שהוזמנו לכתובות שלך</h2>
            <p className='text-text-secondary text-sm'>תוכל לנהל, לעקוב ולבצע פעולות בכל אחת מהחבילות שלך</p>
          </div>
        </div>

        {/* Segmented Control */}
        <div className='space-y-4'>
          <SegmentedButton
            options={segmentedOptions}
            value={selectedTab}
            onChange={value => onSetSelectedTab(value as ViewEnum)}
            className='w-full'
          />

          {/* Action Buttons */}
          {!!pkgsByTab?.length && (
            <div className='flex gap-4'>
              <ClientPkgsFilter
                statusOptions={pkgStatuses}
                warehouses={availableWarehouses}
                value={filter}
                onChange={setFilter}
                currentView={selectedTab}
                isUniteShown={!!isUniteShown}
              />
            </div>
          )}
        </div>

        {/* Pickup Orders Section */}
        {selectedTab === ViewEnum.All && !!filteredPickupOrders?.length && (
          <div className='space-y-4'>
            {filteredPickupOrders.map(order => (
              <PickupOrderPreview key={order.id} order={order} />
            ))}
          </div>
        )}

        {/* Packages List */}
        <div className='space-y-4'>
          {isLoadingUserPkgs ? (
            <div className='py-12 text-center'>
              <div className='mx-auto mb-4 flex h-24 w-24 items-center justify-center'>
                <Spinner className='h-12 w-12' />
              </div>
            </div>
          ) : filteredPackages.length === 0 ? (
            <div className='flex flex-col items-center gap-4 py-12 text-center'>
              <EmptyPkgsIcon className='' />
              <p className='text-text-secondary'>
                {selectedTab === ViewEnum.Identify ? 'הכול מסודר! אין חבילות שממתינות לזיהוי כרגע' : 'הכול מסודר! אין חבילות שנשלחו כרגע'}
              </p>
            </div>
          ) : (
            <>
              {selectedTab === ViewEnum.Identify && (
                <div className='rounded-lg bg-white p-4'>
                  <div className='flex items-center gap-2'>
                    <InfoIcon className='text-text-secondary' />
                    <p className='text-text-secondary text-sm'>
                      יש לנו חבילות שלא הצלחנו לשייך להזמנה שלך. כדי להשלים את תהליך השילוח, נבקש ממך לאשר או לבדוק את הפרטים.
                    </p>
                  </div>
                </div>
              )}
              {filteredPackages.map(pkg => (
                <PkgPreview key={pkg.id} pkg={pkg} currentView={selectedTab} onPkgPayment={handlePackagePayment} onPkgIdentify={() => {}} />
              ))}
            </>
          )}
        </div>
      </div>
      <ConfirmDialog
        isOpen={!!splitPkg}
        onClose={() => {
          navigate(`${RoutePath.ReceiptUpload}/${splitPkg!.id}`)
          setSplitPkg(null)
        }}
        confirmText='עבור לאיחוד חבילות'
        cancelText='המשך לשליחה'
        onConfirm={() => {
          navigate(RoutePath.ClientPkgUnite)
        }}
        content='שמנו לב שהחבילה שלך מפוצלת - תוכל לאחד את החבילות המפוצלות בחינם!'
        title='שים לב!'
      />
    </div>
  )
}
